import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl md:text-3xl text-ink mb-4">
      <span className="text-gold tabular-nums mr-3">{n}.</span>{title}
    </h2>
    <div className="text-ink-muted leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      <header className="border-b border-gold/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Back to ScaleIQ
          </Link>
          <Logo size="sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="overline text-gold mb-3">Legal</div>
        <h1 className="font-display text-5xl md:text-6xl font-light mb-3">Terms of Service</h1>
        <p className="text-sm text-ink-muted mb-12">Effective Date: June 30, 2026</p>

        <p className="text-ink-muted leading-relaxed mb-12">
          Welcome to ScaleIQ ("Company," "we," "our," or "us"). By accessing or using the ScaleIQ website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our website or services.
        </p>

        <Section n="1" title="Services">
          <p>ScaleIQ provides business assessments, reports, recommendations, consulting services, and related business optimization resources.</p>
          <p>All recommendations and reports are provided for informational purposes only and do not guarantee specific business outcomes or financial results.</p>
        </Section>

        <Section n="2" title="Eligibility">
          <p>You must be at least 18 years old to use this website or purchase services from ScaleIQ.</p>
        </Section>

        <Section n="3" title="Purchases and Payments">
          <p>By purchasing a product or service from ScaleIQ, you agree to pay all applicable fees.</p>
          <p>All payments are due at the time of purchase unless otherwise agreed in writing.</p>
          <p>Payments are processed through secure third-party payment providers.</p>
        </Section>

        <Section n="4" title="Refund Policy">
          <p>Due to the customized nature of business assessments, reports, and consulting services, all sales are final and non-refundable unless otherwise stated in writing.</p>
          <p>ScaleIQ reserves the right, at its sole discretion, to issue refunds on a case-by-case basis.</p>
        </Section>

        <Section n="5" title="No Guarantee of Results">
          <p>ScaleIQ provides recommendations, strategies, and insights based on the information provided by users.</p>
          <p>We do not guarantee any specific increase in revenue, profitability, customer acquisition, operational improvements, or other business results.</p>
          <p>Business outcomes depend on many factors beyond our control, including implementation, market conditions, competition, and management decisions.</p>
        </Section>

        <Section n="6" title="User Responsibilities">
          <p>You agree that:</p>
          <ul className="list-none space-y-2 pl-0">
            <li className="flex gap-3"><span className="text-gold">·</span>All information you provide is accurate and complete.</li>
            <li className="flex gap-3"><span className="text-gold">·</span>You will use the website only for lawful purposes.</li>
            <li className="flex gap-3"><span className="text-gold">·</span>You will not attempt to disrupt, damage, or gain unauthorized access to the website or its systems.</li>
            <li className="flex gap-3"><span className="text-gold">·</span>You will not copy, reproduce, or redistribute ScaleIQ materials without written permission.</li>
          </ul>
        </Section>

        <Section n="7" title="Intellectual Property">
          <p>All website content, reports, graphics, logos, text, software, and materials are the exclusive property of ScaleIQ and are protected by applicable intellectual property laws.</p>
          <p>You may use purchased reports for your internal business purposes only.</p>
          <p>You may not copy, resell, distribute, or create derivative works from our materials without prior written consent.</p>
        </Section>

        <Section n="8" title="Disclaimer">
          <p>The information provided by ScaleIQ is for educational and informational purposes only.</p>
          <p>ScaleIQ is not providing legal, tax, accounting, investment, or other professional advice. Users should consult qualified professionals regarding their specific circumstances.</p>
        </Section>

        <Section n="9" title="Limitation of Liability">
          <p>To the fullest extent permitted by law, ScaleIQ shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from the use of our website, reports, recommendations, or services.</p>
          <p>Our total liability for any claim related to our services shall not exceed the amount paid by the customer for the specific service giving rise to the claim.</p>
        </Section>

        <Section n="10" title="Third-Party Services">
          <p>Our website may integrate with or link to third-party services and websites. ScaleIQ is not responsible for the content, policies, or practices of any third-party websites or services.</p>
        </Section>

        <Section n="11" title="Changes to Services">
          <p>ScaleIQ reserves the right to modify, suspend, or discontinue any part of the website or services at any time without prior notice.</p>
        </Section>

        <Section n="12" title="Governing Law">
          <p>These Terms shall be governed by and interpreted in accordance with the laws of the State of Texas, without regard to conflict of law principles.</p>
          <p>Any disputes arising from these Terms shall be resolved exclusively in the state or federal courts located in Texas.</p>
        </Section>

        <Section n="13" title="Changes to These Terms">
          <p>ScaleIQ may update these Terms from time to time. Updated versions will be posted on this page with a revised effective date.</p>
          <p>Continued use of the website after changes are posted constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section n="14" title="Contact Information">
          <p>If you have questions regarding these Terms, please contact:</p>
          <div className="border border-gold/20 p-5 mt-3">
            <div className="font-display text-xl text-ink mb-1">ScaleIQ</div>
            <div>Email: <a href="mailto:kevin@tryscaleiq.com" className="text-gold hover:underline">kevin@tryscaleiq.com</a></div>
            <div>Website: <a href="https://tryscaleiq.com" className="text-gold hover:underline">https://tryscaleiq.com</a></div>
          </div>
        </Section>

        <div className="mt-16 pt-8 border-t border-gold/10 text-xs text-ink-muted flex justify-between">
          <Link to="/privacy" className="hover:text-ink">Privacy Policy →</Link>
          <Link to="/" className="hover:text-ink">Back to ScaleIQ →</Link>
        </div>
      </main>
    </div>
  );
}
